// Best-effort runtime device info for the pairing screen. Unlike the native
// Android app, a browser cannot read the MAC address at all, and the LAN IP /
// network type are only available on some platforms. Everything here degrades
// gracefully to null when the browser does not expose it.

interface NetworkInformationLike {
  type?: string; // 'ethernet' | 'wifi' | 'cellular' | 'none' | 'unknown' | ...
  effectiveType?: string;
}

export type NetworkKind = 'ethernet' | 'wifi' | 'cellular' | null;

// Network type via the Network Information API (where supported).
export function getNetworkKind(): NetworkKind {
  const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  switch (conn?.type) {
    case 'ethernet':
      return 'ethernet';
    case 'wifi':
      return 'wifi';
    case 'cellular':
      return 'cellular';
    default:
      return null;
  }
}

// Public IP as seen by our own server (same mechanism as "what is my IP" sites:
// the server reads it off the connection and echoes it back). Same-origin, no
// third party. Returns null when offline or the endpoint is unavailable.
export async function getPublicIp(timeoutMs = 2500): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch('/__ip', { signal: controller.signal, cache: 'no-store' });
    clearTimeout(timer);
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    const looksLikeIp = /\d{1,3}(?:\.\d{1,3}){3}/.test(text) || text.includes(':');
    return looksLikeIp ? text : null;
  } catch {
    return null;
  }
}

// Attempt to discover the local IPv4 address via WebRTC host candidates. Returns
// null if blocked, masked by mDNS (e.g. "abcd.local"), or unsupported.
export function getLocalIp(timeoutMs = 1500): Promise<string | null> {
  return new Promise((resolve) => {
    let pc: RTCPeerConnection;
    try {
      pc = new RTCPeerConnection({ iceServers: [] });
    } catch {
      resolve(null);
      return;
    }

    let done = false;
    const finish = (ip: string | null) => {
      if (done) return;
      done = true;
      try {
        pc.close();
      } catch {
        /* noop */
      }
      resolve(ip);
    };

    const ipv4 = /(\d{1,3}(?:\.\d{1,3}){3})/;
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const match = ipv4.exec(e.candidate.candidate);
      const ip = match?.[1];
      if (ip && ip !== '0.0.0.0') finish(ip);
    };

    try {
      pc.createDataChannel('probe');
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => finish(null));
    } catch {
      finish(null);
      return;
    }

    setTimeout(() => finish(null), timeoutMs);
  });
}
